/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type Store from "@ember-data/store";
import type ModelRegistry from "@ember-data/types/registries/model";
import { action } from "@ember/object";
import { service } from "@ember/service";
import Component from "@glimmer/component";
import type { EmberChangeset } from "ember-changeset";
import type MedicationPresentation from "parog-web/models/medication-presentation";

const currentComorbChangesetKey = "comorbidities";
const ignoredComorbChangesetKey = "ignoredComorbidities";

interface Args {
  patient: ModelRegistry["patient"];
  changeset: EmberChangeset;
}

export default class PathologyDetectionComponent extends Component<Args> {
  @service declare store: Store;
  get patient() {
    return this.args.patient;
  }
  get changeset() {
    return this.args.changeset;
  }

  @service icd10;

  get currentSelectedComorbidities(): string[] {
    return this.changeset.get(currentComorbChangesetKey) || [];
  }

  get currentIgnoredComorbidities(): string[] {
    return this.changeset.get(ignoredComorbChangesetKey) || [];
  }

  /**
   * This method is a fake pathology detection method used only in demo ENV
   */
  get detectedPathologies(): string[] {
    let atcFoundDate: Date | null = null;
    const hasDeliveries =
      this.args.patient.drugsAndDevicesRefundedJournalEvents &&
      this.args.patient.drugsAndDevicesRefundedJournalEvents.some(je =>
        je.prestations.some(prestation => {
          const medicationPresentation = this.store.peekRecord(
            "medication-presentation",
            prestation.sourceValue
          ) as MedicationPresentation;
          if (
            medicationPresentation.medicationSpecialty.medicationMolecule.atc.startsWith(
              "A10"
            )
          ) {
            // If we found the code for the second time at a different day
            if (
              atcFoundDate &&
              atcFoundDate.toDateString() != je.occurredAt.toDateString()
            ) {
              return true;
            }
            atcFoundDate = je.occurredAt;
          }
          return false;
        })
      );

    if (hasDeliveries) {
      return ["E10", "E11"].filter(code => {
        return (
          !this.currentSelectedComorbidities.includes(code) &&
          !this.currentIgnoredComorbidities.includes(code)
        );
      });
    }

    return [];
  }

  @action
  addComorbidity(code: string): void {
    this.changeset.set(currentComorbChangesetKey, [
      ...this.currentSelectedComorbidities,
      code
    ]);
  }

  @action
  ignoreComorbidity(code: string): void {
    this.changeset.set(ignoredComorbChangesetKey, [
      ...this.currentIgnoredComorbidities,
      code
    ]);
  }

  @action
  addComorbidities(): void {
    this.changeset.set(currentComorbChangesetKey, [
      ...this.currentSelectedComorbidities,
      ...this.detectedPathologies
    ]);
  }
}
